// src/pages/inventory/StockAdjustment.js
// ZENO ERP — Stock Adjustment (Create)
// Creates pending adjustment — inventory NOT changed until approved
// Roles: Store + SM + Owner

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, query, where, addDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getCurrentStock } from "../../services/inventoryService";

const ADJUSTMENT_REASONS = [
  "Physical Count Correction",
  "Damaged / Expired Stock",
  "Opening Balance Entry",
  "Theft / Loss",
  "Found Extra Stock",
  "System Error Correction",
  "Other",
];

export default function StockAdjustment() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [fetchingItems, setFetchingItems] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);
  const [currentStock, setCurrentStock] = useState(0);
  const [newStock,     setNewStock]     = useState(0);
  const [reason,       setReason]       = useState("");
  const [notes,        setNotes]        = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { if (companyId) fetchItems(); }, []);
  // ── Fetch MATERIAL + CONSUMABLE items only ─────────────
  const fetchItems = async () => {
    try {
      setFetchingItems(true);
      const snap = await getDocs(query(
        collection(db, "inventory_summary"),
        where("company_id", "==", companyId)
      ));
      // NEW — is_active check added
const raw = snap.docs
  .map(d => ({ id: d.id, ...d.data() }))
  .filter(item => {
    if (!item.item_name) return false;
    if (item.is_active === false) return false;          // ← NEW
    const cat  = (item.category || "").toUpperCase();
    const code = (item.item_code || "").toUpperCase();
    if (["TOOLS", "ASSET", "ASSETS"].includes(cat)) return false;
    if (code.startsWith("AS-") || code.startsWith("TL-") || code.startsWith("AST-")) return false;
    return true;
  });

      // Deduplicate by item_name — keep doc with highest stock + valid item_code
      // NEW — dedup by item_id primary, item_name fallback (same pattern everywhere)
const deduped = {};
raw.forEach(item => {
  const key = item.item_id
    ? item.item_id
    : (item.item_name || "").toLowerCase().trim();
  if (!key) return;
  if (!deduped[key]) {
    deduped[key] = item;
  } else {
    const existingStock = Number(deduped[key].qty || deduped[key].current_stock || 0);
    const thisStock     = Number(item.qty         || item.current_stock         || 0);
    if (thisStock > existingStock) deduped[key] = item;
  }
});

      const list = Object.values(deduped)
        .sort((a, b) => (a.item_name || "").localeCompare(b.item_name || ""));
      setItems(list);
    } catch (err) {
      console.error("fetchItems error:", err);
    } finally {
      setFetchingItems(false);
    }
  };

  // ── Select item → load current stock ───────────────────
  const handleItemSelect = async (itemId) => {
    const item = items.find(i => i.id === itemId);
    if (!item) { setSelectedItem(null); setCurrentStock(0); setNewStock(0); return; }
    setSelectedItem(item);
    const stockData = await getCurrentStock(item.item_id || item.id, companyId);
    const qty = Number(stockData.qty || stockData.current_stock || 0);
    setCurrentStock(qty);
    setNewStock(qty);
  };

  const difference = newStock - currentStock;

  // ── Generate ADJ number ─────────────────────────────────
  const generateADJNo = async () => {
    const snap = await getDocs(query(
      collection(db, "stock_adjustments"),
      where("company_id", "==", companyId)
    ));
    return `ADJ-${String(snap.size + 1).padStart(4, "0")}`;
  };

  // ── Submit for approval ─────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedItem)     { alert("Select an item"); return; }
    if (!reason)           { alert("Select adjustment reason"); return; }
    if (newStock < 0)      { alert("New stock cannot be negative"); return; }
    if (difference === 0)  { alert("No change — new stock equals current stock"); return; }

    setLoading(true);
    try {
      const adjNo = await generateADJNo();

      await addDoc(collection(db, "stock_adjustments"), {
        company_id:       companyId,
        adjustment_no:    adjNo,
        item_id:          selectedItem.item_id || selectedItem.id,
        item_code:        selectedItem.item_code || "",
        item_name:        selectedItem.item_name,
        category:         selectedItem.category || "",
        unit:             selectedItem.unit || "nos",
        current_stock:    currentStock,
        new_stock:        newStock,
        difference,
        adjustment_type:  difference > 0 ? "increase" : "decrease",
        reason,
        notes:            notes.trim(),
        status:           "pending",
        submitted_by:     user.uid || user.id || "",
        submitted_by_name: user.name || "",
        submitted_at:     serverTimestamp(),
        created_at:       serverTimestamp(),
      });

      alert(`✅ Adjustment ${adjNo} submitted for approval`);
      setSelectedItem(null);
      setCurrentStock(0);
      setNewStock(0);
      setReason("");
      setNotes("");

    } catch (err) {
      console.error("StockAdjustment submit error:", err);
      alert(err.message || "Failed to submit adjustment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-800 mb-4 block">
        ← Back
      </button>

      <div className="flex justify-between items-center mb-5">
        <h1 className="text-xl font-bold text-slate-800">Stock Adjustment</h1>
        <button onClick={() => navigate("/stock-adjustment-approval")}
          className="text-xs text-amber-600 font-semibold border border-amber-300 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 transition">
          Pending Approvals →
        </button>
      </div>

      {/* SELECT ITEM */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
        <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
          Select Item
        </label>
        {fetchingItems ? (
          <p className="text-sm text-slate-400">Loading items...</p>
        ) : (
          <select
            value={selectedItem?.id || ""}
            onChange={e => handleItemSelect(e.target.value)}
            className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-amber-400 bg-white">
            <option value="">— Select Item —</option>
            {items.map(item => (
              <option key={item.id} value={item.id}>
                {item.item_name} ({item.item_code || "—"})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* STOCK DETAILS */}
      {selectedItem && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Adjustment Details</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Current Stock</label>
              <div className="border border-slate-200 rounded-xl p-3 text-sm bg-slate-50 font-semibold text-slate-700">
                {currentStock} <span className="text-xs text-slate-400">{selectedItem.unit}</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">New Stock *</label>
              <input
                type="number" min="0"
                value={newStock}
                onChange={e => setNewStock(Math.max(0, Number(e.target.value)))}
                className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Difference</label>
              <div className={`border rounded-xl p-3 text-sm font-bold ${
                difference > 0 ? "border-green-200 bg-green-50 text-green-700" :
                difference < 0 ? "border-red-200 bg-red-50 text-red-700" :
                "border-slate-200 bg-slate-50 text-slate-400"
              }`}>
                {difference > 0 ? `+${difference}` : difference} <span className="text-xs font-normal">{selectedItem.unit}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REASON + NOTES */}
      {selectedItem && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
              Reason *
            </label>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-amber-400 bg-white">
              <option value="">— Select Reason —</option>
              {ADJUSTMENT_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
              Notes (optional)
            </label>
            <textarea rows={2} placeholder="Additional details..."
              value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
        </div>
      )}

      {/* SUBMIT */}
      {selectedItem && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4">
          <p className="text-xs text-amber-700">
            ⚠️ This adjustment will be sent for approval. Stock will only change after Manager/Owner approves.
          </p>
        </div>
      )}

      {selectedItem && (
        <button onClick={handleSubmit} disabled={loading}
          className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition disabled:opacity-40">
          {loading ? "Submitting..." : "Submit for Approval"}
        </button>
      )}
    </div>
  );
}